import { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  collection, 
  query, 
  where,
  getDoc,
  doc, 
  setDoc,
  deleteDoc,
  getDocs
} from 'firebase/firestore';
import { 
  getAuth,
  createUserWithEmailAndPassword, 
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { onSnapshot } from 'firebase/firestore';
import { initializeApp, deleteApp } from 'firebase/app';
import { firebaseConfig } from '../firebase';
import DataTableStructure from '../components/DataTableStructure';
import { FaCog } from 'react-icons/fa';
import AdminDashboardUserSettingModal from '../components/AdminDashboardUserSettingModal';

function AdminDashboard() {
    const [pendingUsers, setPendingUsers] = useState([]);
    const [users, setUsers] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [pendingTableSort, setPendingTableSort] = useState([]);
    const [usersTableSort, setUsersTableSort] = useState([]);
    
    // Check if user is admin
    useEffect(() => {
        const checkAdminStatus = async () => {
            const userDoc = await getDoc(doc(db, 'users', currentUser?.uid));
            if (!userDoc.exists() || !userDoc.data().isAdmin) {
                navigate('/dashboard');
            }
        };

        if (currentUser) {
            checkAdminStatus();
        }
    }, [currentUser, navigate]);

    // fetch approved users
    useEffect(() => {
        const usersQuery = query(
            collection(db, 'users'),
            where('isAdmin', '==', false) // Only filter out admins, remove the approved filter
        );
    
        const unsubscribe = onSnapshot(
            usersQuery,
            (snapshot) => {
                setUsers(
                    snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                );
            },
            (error) => {
                console.error('Error fetching users:', error);
                if (error.code === 'permission-denied') {
                    navigate('/dashboard');
                }
            }
        );
    
        return () => unsubscribe();
    }, [navigate]);

    const handleApproval = useCallback(async (userId, approved) => {
        try {
            if (approved) {
                // Initialize secondary app inside the callback
                const secondaryApp = initializeApp(firebaseConfig, 'secondary');
                const secondaryAuth = getAuth(secondaryApp);

                // Get the pending registration data
                const pendingRef = doc(db, 'pendingRegistrations', userId);
                const pendingDoc = await getDoc(pendingRef);
                
                if (!pendingDoc.exists()) {
                    toast.error('Registration request no longer exists');
                    setPendingUsers(pendingUsers.filter(user => user.id !== userId));
                    return;
                }
    
                const userData = pendingDoc.data();
    
                // Check if email already exists in users collection
                const usersRef = collection(db, 'users');
                const emailQuery = query(usersRef, where('email', '==', userData.email));
                const emailSnapshot = await getDocs(emailQuery);
    
                if (!emailSnapshot.empty) {
                    toast.error('Email already registered');
                    return;
                }
    
                // Create the user in Firebase Auth using secondary instance
                const userCredential = await createUserWithEmailAndPassword(
                    secondaryAuth, 
                    userData.email, 
                    atob(userData.password)
                );
    
                // Update profile with username
                await updateProfile(userCredential.user, {
                    displayName: userData.username
                });
    
                // Send verification email
                await sendEmailVerification(userCredential.user);
    
                // Create the user document in Firestore
                await setDoc(doc(db, 'users', userCredential.user.uid), {
                    username: userData.username,
                    usernameLower: userData.username.toLowerCase(),
                    email: userData.email,
                    status: 'approved',
                    createdAt: new Date().toISOString(),
                    approved: true,
                    isAdmin: false,
                    displayName: userData.username,
                    loginCount: 0,
                    lastLoginAt: null,
                    membershipLevel: 'free'
                });
    
                // Delete the pending registration
                await deleteDoc(pendingRef);
                
                // Sign out and delete the secondary app
                await secondaryAuth.signOut();
                await deleteApp(secondaryApp);
    
                toast.success('User approved successfully! They will need to verify their email before logging in.');
            } else {
                // If declined, just delete the pending registration
                await deleteDoc(doc(db, 'pendingRegistrations', userId));
                toast.info('Registration request declined');
            }
    
            // Remove the user from the pending list
            setPendingUsers(pendingUsers.filter(user => user.id !== userId));
        } catch (error) {
            console.error('Error handling approval:', error);
            toast.error(error.message || 'Error handling approval');
        }
    }, [pendingUsers]);

    // Update the fetch to get pending registrations instead
    useEffect(() => {
        const unsubscribe = onSnapshot(
            collection(db, 'pendingRegistrations'),
            (snapshot) => {
                setPendingUsers(
                    snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
                );
            },
            (error) => {
                console.error('Error fetching pending users:', error);
                if (error.code === 'permission-denied') {
                    navigate('/dashboard');
                }
            }
        );

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [navigate]);

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedUser(null);
    };

    // Define table pending approval columns
    const pendingColumns = useMemo(
        () => [
            {
                Header: 'NAME',
                accessor: 'username',
                Cell: ({ row }) => (
                    <div className="flex items-center">
                        <div>
                            <div className="text-sm font-medium text-gray-900">{row.original.username}</div>
                            <div className="text-sm text-gray-500">{row.original.email}</div>
                        </div>
                    </div>
                ),
            },
            {
                Header: 'STATUS',
                accessor: 'status',
                Cell: () => (
                    <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                        Pending
                    </span>
                ),
            },
            {
                Header: 'ACTIONS',
                accessor: 'id',
                Cell: ({ value }) => (
                    <div className="space-x-3">
                        <button
                            onClick={() => handleApproval(value, true)}
                            className="text-green-600 hover:text-green-900"
                        >
                            Approve
                        </button>
                        <button
                            onClick={() => handleApproval(value, false)}
                            className="text-red-600 hover:text-red-900"
                        >
                            Decline
                        </button>
                    </div>
                ),
            },
        ],
        [handleApproval]
    );

    // Define table users columns
    const usersColumns = useMemo(
        () => [
            {
                Header: 'NAME',
                accessor: 'username',
                Cell: ({ row }) => (
                    <div className="flex items-center">
                        <div>
                            <div className="text-sm font-medium text-gray-900">{row.original.username}</div>
                            <div className="text-sm text-gray-500">{row.original.email}</div>
                        </div>
                    </div>
                ),
            },
            {
                Header: 'LOGIN INFO',
                accessor: 'loginCount',
                Cell: ({ row }) => (
                    <div className="text-sm">
                        <div className="text-gray-900">
                            Logins: {row.original.loginCount || 0}
                        </div>
                        <div className="text-gray-500">
                            Last login: {row.original.lastLoginAt 
                                ? new Date(row.original.lastLoginAt.toDate()).toLocaleString()
                                : 'Never'}
                        </div>
                    </div>
                ),
            },
            {
                Header: 'MEMBERSHIP',
                accessor: 'membershipLevel',
                Cell: ({ value }) => (
                    <span className={`px-3 pt-1 pb-1.5 inline-flex text-xs leading-5 font-semibold rounded-full shadow-sm ${
                        value === 'premium' 
                            ? 'bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-200 text-yellow-900'
                            : value === 'promo'
                            ? 'bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-500 text-white'
                            : value === 'free'
                            ? 'bg-gradient-to-r from-gray-200 via-gray-100 to-gray-50 text-gray-800'
                            : 'bg-gradient-to-r from-red-200 via-red-100 to-red-50 text-red-800'
                    }`}>
                        {value === null || value === undefined ? 'null' : value}
                    </span>
                ),
            },
            {
                Header: 'STATUS',
                accessor: 'status',
                Cell: ({ value }) => (
                    <span className={`px-3 pt-1 pb-1.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        value === 'suspended' 
                            ? 'bg-red-100 text-red-800'
                            : 'bg-green-100 text-green-800'
                    }`}>
                        {value || 'Approved'}
                    </span>
                ),
            },
            {
                Header: 'CREATED AT',
                accessor: 'createdAt',
                Cell: ({ value }) => (
                    <div className="text-sm text-gray-900">
                        {new Date(value).toLocaleDateString()}
                    </div>
                ),
            },
            {
                Header: 'SETTINGS',
                accessor: 'settings',
                Cell: ({ row }) => (
                    <button
                        onClick={() => {
                            setSelectedUser(row.original);
                            setIsModalOpen(true);
                        }}
                        className="text-gray-600 hover:text-gray-900"
                    >
                        <FaCog className="h-5 w-5" />
                    </button>
                ),
            },
        ],
        []
    );

    return (
        <div className="min-h-screen bg-gray-50 py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between items-center">
                    <h1 className="text-2xl font-semibold text-gray-900">Admin Dashboard</h1>
                    <button
                        onClick={() => navigate('/dashboard')}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                    >
                        Back to Dashboard
                    </button>
                </div>

                {/* Pending Approvals Table */}
                <div className="mt-8">
                    <h2 className="text-xl font-medium text-gray-900 mb-4">Pending Approvals</h2>
                    <DataTableStructure
                        columns={pendingColumns}
                        data={pendingUsers}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        initialSortBy={pendingTableSort}
                        onSortChange={setPendingTableSort}
                    />
                </div>

                {/* Registered Users Table */}
                <div className="mt-8">
                    <h2 className="text-xl font-medium text-gray-900 mb-4">Registered Users</h2>
                    <DataTableStructure
                        columns={usersColumns}
                        data={users}
                        globalFilter={globalFilter}
                        setGlobalFilter={setGlobalFilter}
                        initialSortBy={usersTableSort}
                        initialPageSize={10}
                        onSortChange={setUsersTableSort}
                    />
                </div>
            </div>

            {/* User Settings Modal */}
            <AdminDashboardUserSettingModal
                isOpen={isModalOpen}
                onClose={closeModal}
                user={selectedUser}
            />
        </div>
    );
}

export default AdminDashboard;